import { IMAGE_BASE_URL } from "./constants";

export function formatPrice(value, currency = "") {
  const n = Number(value || 0);
  return currency ? `${currency} ${n.toFixed(2)}` : n.toFixed(2);
}

// Matches denzo-web's DetailContent/Item.js media convention: card thumbnails
// live under .../product/card/, the cover image and every extra
// product_images entry are all full-resolution files under .../product/cover/.
export function productCardImage(storeId, filename) {
  return filename ? `${IMAGE_BASE_URL}media/${storeId}/product/card/${filename}` : null;
}

export function productCoverImage(storeId, filename) {
  return filename ? `${IMAGE_BASE_URL}media/${storeId}/product/cover/${filename}` : null;
}

// A product's media gallery: cover image, then every product_images entry
// (already full-resolution filenames — see productCoverImage above), each
// tagged as an 'image' slide. video_url (products.video_url) is a plain
// external URL (YouTube/Vimeo link or a direct file), not a stored filename,
// so it's used as-is rather than run through productCoverImage — appended
// last so the still images load/paint first.
export function productMedia(product, storeId) {
  if (!product) return [];
  const slides = [];
  if (product.cover_image) {
    slides.push({ type: "image", src: productCoverImage(storeId, product.cover_image), key: "cover" });
  }
  (product.product_images || []).forEach((img, i) => {
    if (img?.image) slides.push({ type: "image", src: productCoverImage(storeId, img.image), key: `img_${img.id ?? i}` });
  });
  if (product.video_url) {
    slides.push({ type: "video", src: product.video_url, key: "video" });
  }
  return slides;
}

const YOUTUBE_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
const VIMEO_RE = /vimeo\.com\/(\d+)/;

// video_url can be a YouTube/Vimeo share link (needs an <iframe> embed) or a
// direct video file URL (a plain <video> tag handles that). Returns null for
// a direct file so the caller knows to fall back to <video>.
export function embeddableVideoUrl(url) {
  if (!url) return null;
  const yt = url.match(YOUTUBE_RE);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(VIMEO_RE);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

// product.description comes from denzo-admin's rich-text editor as raw HTML.
// Stripped to plain text and split on block-level boundaries (paragraphs,
// line breaks, list items) into separate lines rather than one flat run of
// text — enough structure to read a multi-paragraph description comfortably
// without rendering raw HTML from store-authored content.
export function descriptionParagraphs(html) {
  if (!html) return [];
  const withBreaks = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, "\n");
  const tmp = document.createElement("div");
  tmp.innerHTML = withBreaks;
  const text = tmp.textContent || tmp.innerText || "";
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

// Set from the POS product settings ("Online Store"): listed, but can't be
// ordered. ("unavailable" products aren't listed here at all.)
export function isOutOfStock(product) {
  return product?.online_status === "out_of_stock";
}

// A product's own listing price, honoring an active offer — same rule the
// backend's ProductResource/OrderController addToCart apply.
export function productPrice(product) {
  return product?.offer_price > 0 ? Number(product.offer_price) : Number(product?.price || 0);
}

export function productStrikePrice(product) {
  return product?.offer_price > 0 ? Number(product.price) : null;
}

// An order_item's unit price, honoring its selected option — mirrors
// OrderController@addToCart's own unit_price calc (option price wins over
// the product's own price when an option was chosen).
function itemUnitPrice(item) {
  if (item.option?.price != null) return Number(item.option.price);
  return productPrice(item.product);
}

function itemAddonsTotal(item) {
  return (item.addons || []).reduce((sum, a) => sum + Number(a.price || 0), 0);
}

// Client-side estimate of a line's total, used only for optimistic UI before
// the server's own addToCart response (which carries the authoritative
// unit_price/price/tax/total_price) lands. Deliberately excludes tax, unlike
// the backend's total_price — good enough for an optimistic subtotal.
export function estimateLineTotal(item) {
  return (itemUnitPrice(item) + itemAddonsTotal(item)) * item.quantity;
}

// A server order_item already carries its authoritative total_price (unit +
// addons + tax) once a request round-trips — prefer that over any client
// estimate whenever it's present.
export function lineDisplayTotal(item) {
  return item.total_price != null ? Number(item.total_price) : estimateLineTotal(item);
}

// kot_printed_qty > 0 means the kitchen has already been sent at least one
// unit of this line (order_items.kot_printed_qty — shared with denzo-pos/
// denzo-waiter, populated once store staff KOT-prints the order on their
// side). Once that's happened, a guest reducing or removing the line here
// would silently diverge from what the kitchen is already cooking, so it's
// locked read-only instead — matches the requirement that this app never
// needs a cancel-reason flow the way the staff waiter app does.
export function isKotLocked(item) {
  return Number(item?.kot_printed_qty || 0) > 0;
}

// The lines that saving this edit will actually put in front of the kitchen:
// quantity - kot_printed_qty per line (0/undefined kot_printed_qty for a line
// added this session counts in full) — mirrors the backend's own KOT ticket
// calculation (CommonController@getKOTPrintLine), so this always matches what
// gets printed once Save triggers the store's auto-KOT-print setting.
// Products switched off for KOT (product.kot_print, set from the POS product
// settings) never go to the kitchen, so they're left out.
export function kotPrintLines(order) {
  return (order?.order_items || [])
    .filter((item) => ![false, 0, "0", "false"].includes(item.product?.kot_print))
    .map((item) => ({ name: item.product?.name || "", qty: (item.quantity || 0) - (item.kot_printed_qty || 0) }))
    .filter((item) => item.qty > 0);
}

export function orderItemCount(order) {
  return (order?.order_items || []).reduce((n, i) => n + (i.quantity || 0), 0);
}

export function orderTotal(order) {
  if (!order) return 0;
  if (order.grand_total != null && Number(order.grand_total) > 0) return Number(order.grand_total);
  return (order.order_items || []).reduce((sum, i) => sum + lineDisplayTotal(i), 0);
}
